import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

export interface SocialMetaTags {
  title: string;
  description: string;
  image: string;
  url: string;
  type?: string;
  siteName?: string;
  author?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MetaService {

  constructor(private meta: Meta, private title: Title) {}

  /**
   * Set comprehensive meta tags for social sharing
   * Works both on client and server side
   */
  setSocialMetaTags({ title, description, image, url, type, siteName, author }: SocialMetaTags): void {
    // Only use API values - no fallbacks
    const sanitizedTitle = title ? this.sanitizeText(title) : '';
    const sanitizedDescription = description ? this.sanitizeText(description).replace(/\s+/g, ' ').trim() : '';
    const canonicalUrl = url || environment.frontendUrl || 'https://www.superlawyer.in';

    // Only set title if API provides it (no fallbacks)
    if (sanitizedTitle) {
      this.title.setTitle(sanitizedTitle);
      this.setOrUpdateMetaTag('property="og:title"', { property: 'og:title', content: sanitizedTitle });
    }

    // Only set description if API provides it (no fallbacks)
    if (sanitizedDescription) {
      this.setOrUpdateMetaTag('name="description"', { name: 'description', content: sanitizedDescription });
      this.setOrUpdateMetaTag('property="og:description"', { property: 'og:description', content: sanitizedDescription });
    }

    this.setOrUpdateMetaTag('name="author"', { name: 'author', content: author || 'SuperLawyer Team' });
    
    // Ensure image URL is absolute and set og:image
    // If image already starts with http:// or https://, use it directly
    // Otherwise, convert relative URL to absolute
    let absoluteImageUrl = image || '';
    if (absoluteImageUrl && !absoluteImageUrl.startsWith('http://') && !absoluteImageUrl.startsWith('https://')) {
      absoluteImageUrl = `${canonicalUrl.split('/').slice(0, 3).join('/')}${absoluteImageUrl.startsWith('/') ? '' : '/'}${absoluteImageUrl}`;
    }
    
    // Always set og:image - required for WhatsApp/Facebook previews
    if (absoluteImageUrl) {
      this.setOrUpdateMetaTag('property="og:image"', { property: 'og:image', content: absoluteImageUrl });
    }
    this.setOrUpdateMetaTag('property="og:url"', { property: 'og:url', content: canonicalUrl });
    this.setOrUpdateMetaTag('property="og:type"', { property: 'og:type', content: type || 'website' });
    this.setOrUpdateMetaTag('property="og:site_name"', { property: 'og:site_name', content: siteName || 'SuperLawyer' });
    this.setOrUpdateMetaTag('property="og:locale"', { property: 'og:locale', content: 'en_US' });

    // Enhanced image meta tags for better LinkedIn support
    if (absoluteImageUrl) {
      // Detect image type based on file extension
      const imageType = this.getImageMimeType(absoluteImageUrl);
      const secureImageUrl = absoluteImageUrl.replace('http://', 'https://');
      
      // Detect if image is a favicon (both .ico files and PNG favicons should use square dimensions)
      // Check for favicon in filename or path - this includes favicon-1.png, favicon.png, etc.
      const lowerImage = absoluteImageUrl.toLowerCase();
      const isFavicon = lowerImage.endsWith('.ico') || 
                       lowerImage.includes('/images/favicon.ico') || 
                       lowerImage.includes('/favicon.ico') ||
                       lowerImage.includes('/favicon-') || // Matches favicon-1.png, favicon.png, etc.
                       (lowerImage.includes('favicon') && (lowerImage.endsWith('.png') || lowerImage.endsWith('.jpg') || lowerImage.endsWith('.jpeg')));
      
      this.setOrUpdateMetaTag('property="og:image:url"', { property: 'og:image:url', content: absoluteImageUrl });
      this.setOrUpdateMetaTag('property="og:image:secure_url"', { property: 'og:image:secure_url', content: secureImageUrl });
      this.setOrUpdateMetaTag('property="og:image:type"', { property: 'og:image:type', content: imageType });
      
      // Set width/height - WhatsApp REQUIRES this for previews to work
      // WhatsApp minimum: 200x200px (we use this for favicons to ensure preview shows)
      // For regular images, use recommended: 1200x630px
      if (isFavicon) {
        // Use WhatsApp minimum dimensions (200x200) for favicons
        // This ensures WhatsApp shows the preview (it requires width/height)
        this.setOrUpdateMetaTag('property="og:image:width"', { property: 'og:image:width', content: '200' });
        this.setOrUpdateMetaTag('property="og:image:height"', { property: 'og:image:height', content: '200' });
      } else {
        // For regular images (blog posts, etc.), use standard social media dimensions
        // WhatsApp/LinkedIn recommended: 1200x630 (1.91:1 ratio)
        this.setOrUpdateMetaTag('property="og:image:width"', { property: 'og:image:width', content: '1200' });
        this.setOrUpdateMetaTag('property="og:image:height"', { property: 'og:image:height', content: '630' });
      }
      
      this.setOrUpdateMetaTag('property="og:image:alt"', { property: 'og:image:alt', content: `${siteName || 'SuperLawyer'} Logo` });
    }

    // Twitter Card meta tags
    this.setOrUpdateMetaTag('name="twitter:card"', { name: 'twitter:card', content: 'summary_large_image' });
    this.setOrUpdateMetaTag('name="twitter:site"', { name: 'twitter:site', content: '@SuperLawyer' });
    this.setOrUpdateMetaTag('name="twitter:creator"', { name: 'twitter:creator', content: '@SuperLawyer' });
    // Only set Twitter title/description if API provides them (no fallbacks)
    if (sanitizedTitle) {
      this.setOrUpdateMetaTag('name="twitter:title"', { name: 'twitter:title', content: sanitizedTitle });
    }
    if (sanitizedDescription) {
      this.setOrUpdateMetaTag('name="twitter:description"', { name: 'twitter:description', content: sanitizedDescription });
    }
    if (absoluteImageUrl) {
      this.setOrUpdateMetaTag('name="twitter:image"', { name: 'twitter:image', content: absoluteImageUrl });
      this.setOrUpdateMetaTag('name="twitter:image:alt"', { name: 'twitter:image:alt', content: `${siteName || 'SuperLawyer'} Logo` });
    }

    // LinkedIn-specific meta tags
    this.setOrUpdateMetaTag('property="article:author"', { property: 'article:author', content: author || 'SuperLawyer Team' });
    this.setOrUpdateMetaTag('property="article:publisher"', { property: 'article:publisher', content: 'SuperLawyer' });

    // Additional meta tags for better SEO and social sharing
    this.setOrUpdateMetaTag('name="robots"', { name: 'robots', content: 'index, follow' });
    this.setOrUpdateMetaTag('name="viewport"', { name: 'viewport', content: 'width=device-width, initial-scale=1' });
    this.setCanonicalUrl(canonicalUrl);
  }

  /**
   * Remove all social meta tags
   */
  removeSocialMetaTags(): void {
    const metaTagsToRemove = [
      'property="og:title"',
      'property="og:description"',
      'property="og:image"',
      'property="og:url"',
      'property="og:type"',
      'property="og:site_name"',
      'property="og:locale"',
      'property="og:image:width"',
      'property="og:image:height"',
      'property="og:image:type"',
      'property="og:image:secure_url"',
      'name="twitter:card"',
      'name="twitter:title"',
      'name="twitter:description"',
      'name="twitter:image"',
      'name="description"',
      'name="author"'
    ];

    metaTagsToRemove.forEach(selector => {
      this.meta.removeTag(selector);
    });
  }

  /**
   * Set default meta tags for the application
   */
  setDefaultMetaTags(): void {
    this.setSocialMetaTags({
      title: 'SuperLawyer - Legal Interviews and Insights',
      description: 'SuperLawyer provides exclusive legal interviews, insights, and career guidance for law professionals and students.',
      image: `${environment.frontendUrl || 'https://www.superlawyer.in'}/images/logo.svg`,
      url: environment.frontendUrl || 'https://www.superlawyer.in',
      type: 'website',
      siteName: 'SuperLawyer',
      author: 'SuperLawyer Team'
    });
  }

  /**
   * Update specific meta tag
   */
  updateMetaTag(selector: string, content: string): void {
    this.meta.updateTag({ property: selector, content });
  }

  /**
   * Get current meta tag content
   */
  getMetaTag(selector: string): string | null {
    const tag = this.meta.getTag(selector);
    return tag ? tag.content : null;
  }

  private setOrUpdateMetaTag(selector: string, tag: { [key: string]: string }): void {
    const existingTag = this.meta.getTag(selector);
    if (existingTag) {
      this.meta.updateTag(tag, selector);
    } else {
      // Use forceCreation: true for SSR compatibility
      // This ensures meta tags are added even during server-side rendering
      this.meta.addTag(tag, true);
    }
  }

  private sanitizeText(text: string): string {
    return text.replace(/[\x00-\x1F\x7F]/g, '').trim();
  }

  /**
   * Get MIME type based on image file extension
   * Check file extensions first, then check for favicon path
   */
  private getImageMimeType(imageUrl: string): string {
    const lowerUrl = imageUrl.toLowerCase();
    
    // Check file extensions first (most specific)
    if (lowerUrl.endsWith('.ico')) {
      return 'image/x-icon';
    } else if (lowerUrl.endsWith('.png')) {
      return 'image/png';
    } else if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg')) {
      return 'image/jpeg';
    } else if (lowerUrl.endsWith('.svg')) {
      return 'image/svg+xml';
    } else if (lowerUrl.endsWith('.webp')) {
      return 'image/webp';
    } else if (lowerUrl.endsWith('.gif')) {
      return 'image/gif';
    }
    
    // Fallback: check if it's a favicon path (even without .ico extension)
    if (lowerUrl.includes('/images/favicon.ico') || lowerUrl.includes('/favicon.ico')) {
      return 'image/x-icon';
    }
    
    // Default to PNG if unknown
    return 'image/png';
  }

  private setCanonicalUrl(url: string): void {
    if (!url) {
      return;
    }

    const normalizedUrl = url.split('#')[0];
    this.setOrUpdateMetaTag('rel="canonical"', { rel: 'canonical', href: normalizedUrl });
  }
}
